import React, { useCallback, useEffect, useRef, useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Copy, Download, Printer, X } from 'lucide-react';
import { useBrandedImages, useCompanyBranding } from '../../hooks/useBranding';
import {
    CUSTOMER_QR_SHEET_COPY,
    buildCustomerQrSheetHtml,
    downloadCanvasAsPng,
    loadPosterLogoDataUrl,
    printHtmlDocument,
    renderCustomerQrPosterCanvas,
    resolvePosterLogoPath,
} from '../../utils/customerQrSheetUtils';
import { useDialog } from '../../contexts/DialogContext';

const CustomerQRModal = ({ qrData, loading, error, onClose }) => {
    const { toast } = useDialog();
    const brandedImages = useBrandedImages();
    const { fullName, name } = useCompanyBranding();
    const companyLabel = fullName || name || 'Softforceapps';
    const posterLogoPath = resolvePosterLogoPath(brandedImages);
    const qrWrapRef = useRef(null);
    const logoDataUrlRef = useRef(null);
    const [sheetBusy, setSheetBusy] = useState(false);

    const loginUrl = qrData?.login_url || '';

    useEffect(() => {
        logoDataUrlRef.current = null;
        loadPosterLogoDataUrl(brandedImages)
            .then((dataUrl) => {
                logoDataUrlRef.current = dataUrl;
            })
            .catch(() => {
                logoDataUrlRef.current = null;
            });
    }, [brandedImages]);

    const getQrDataUrl = useCallback(() => {
        const canvas = qrWrapRef.current?.querySelector('canvas');
        if (!canvas) {
            return null;
        }
        return canvas.toDataURL('image/png');
    }, []);

    const buildSheetAssets = useCallback(async () => {
        if (!loginUrl) {
            throw new Error('Login URL is not available');
        }

        let logoDataUrl = logoDataUrlRef.current;
        if (!logoDataUrl) {
            logoDataUrl = await loadPosterLogoDataUrl(brandedImages);
            logoDataUrlRef.current = logoDataUrl;
        }

        const qrDataUrl = getQrDataUrl();
        if (!qrDataUrl) {
            throw new Error('QR code is not ready yet');
        }

        return { logoDataUrl, qrDataUrl, loginUrl, companyFullName: companyLabel };
    }, [loginUrl, brandedImages, getQrDataUrl, companyLabel]);

    const handleCopy = async () => {
        if (!loginUrl) {
            return;
        }
        try {
            await navigator.clipboard.writeText(loginUrl);
            toast.success('Customer login link copied to clipboard');
        } catch {
            toast.error('Could not copy link');
        }
    };

    const handlePrint = async () => {
        setSheetBusy(true);
        try {
            const assets = await buildSheetAssets();
            const html = buildCustomerQrSheetHtml(assets);
            await printHtmlDocument(html);
        } catch (err) {
            console.error('Print failed:', err);
            toast.error(err?.message || 'Could not print. Please try Download instead.');
        } finally {
            setSheetBusy(false);
        }
    };

    const handleDownload = async () => {
        setSheetBusy(true);
        try {
            const assets = await buildSheetAssets();
            const canvas = await renderCustomerQrPosterCanvas(assets);
            await downloadCanvasAsPng(canvas, 'customer-login-qr.png');
        } catch (err) {
            console.error('Download failed:', err);
            toast.error(err?.message || 'Could not download image.');
        } finally {
            setSheetBusy(false);
        }
    };

    const logoSrc = posterLogoPath || null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-md rounded-lg bg-white shadow-xl max-h-[90vh] overflow-y-auto">
                <div className="flex items-center justify-between border-b px-4 py-3 sticky top-0 bg-white z-10">
                    <h2 className="text-lg font-semibold text-gray-900">Customer Login QR</h2>
                    <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-800">
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 text-center">
                    {loading ? (
                        <p className="text-gray-500">Loading...</p>
                    ) : error ? (
                        <p className="text-sm text-red-600">{error}</p>
                    ) : loginUrl ? (
                        <>
                            <div className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm mb-4">
                                <header className="border-b border-gray-100 pb-4 mb-4">
                                    {logoSrc ? (
                                        <img
                                            src={logoSrc}
                                            alt={companyLabel}
                                            className="mx-auto mb-3 max-h-14 max-w-[220px] object-contain"
                                            onError={(e) => {
                                                if (e.currentTarget.src.endsWith('logo.svg')) {
                                                    return;
                                                }
                                                e.currentTarget.src = '/img/sfa/logo.svg';
                                            }}
                                        />
                                    ) : null}
                                    <h3 className="text-xl font-bold text-slate-900">
                                        {CUSTOMER_QR_SHEET_COPY.title}
                                    </h3>
                                    <p className="text-sm text-slate-500 mt-2 leading-relaxed">
                                        {CUSTOMER_QR_SHEET_COPY.subtitle}
                                    </p>
                                </header>

                                <div
                                    ref={qrWrapRef}
                                    className="inline-block rounded-xl border border-gray-200 p-3 bg-white"
                                >
                                    <QRCodeCanvas value={loginUrl} size={220} level="M" includeMargin />
                                </div>

                                <p className="text-xs text-slate-500 break-all font-mono mt-4 leading-relaxed">
                                    {loginUrl}
                                </p>

                                <footer className="border-t border-gray-100 pt-4 mt-4">
                                    <p className="text-xs text-slate-500">{CUSTOMER_QR_SHEET_COPY.footerLine1}</p>
                                    <p className="text-xs text-slate-400 mt-2">
                                        {CUSTOMER_QR_SHEET_COPY.footerLine2(companyLabel)} · ©{' '}
                                        {new Date().getFullYear()}
                                    </p>
                                </footer>
                            </div>

                            <p className="text-xs text-gray-400 mb-2">
                                One QR for all customers · does not expire
                            </p>
                        </>
                    ) : null}
                </div>

                <div className="flex flex-wrap justify-end gap-2 border-t px-4 py-3 sticky bottom-0 bg-white">
                    <button
                        type="button"
                        onClick={handleCopy}
                        disabled={!loginUrl || sheetBusy}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <Copy className="h-4 w-4" />
                        Copy link
                    </button>
                    <button
                        type="button"
                        onClick={handleDownload}
                        disabled={!loginUrl || sheetBusy}
                        className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                    >
                        <Download className="h-4 w-4" />
                        {sheetBusy ? 'Please wait…' : 'Download'}
                    </button>
                    <button
                        type="button"
                        onClick={handlePrint}
                        disabled={!loginUrl || sheetBusy}
                        className="inline-flex items-center gap-1 rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                        <Printer className="h-4 w-4" />
                        {sheetBusy ? 'Please wait…' : 'Print'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CustomerQRModal;
